<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import RadioButton from 'primevue/radiobutton';
import Checkbox from 'primevue/checkbox';
import InputText from 'primevue/inputtext';
import { type FormField, FormFieldChoice, OTHER_CHOICE_CODE } from '@/models';
import { CustomButton, ButtonType } from '@/components';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  }
});

const localField = ref<FormField>(props.field);

const resetChoiceValue = () => {
  if (localField.value.options.maximumChoicesCount == 1) {
    localField.value.choiceValue = FormFieldChoice.createEmpty();
  } else {
    localField.value.options.choices!.forEach(
      choice => (choice.checked = false)
    );
  }
  if (localField.value.otherChoiceValue) {
    localField.value.otherChoiceValue!.value = -1;
    localField.value.otherChoiceValue!.checked = false;
  }
};

const onChoiceClick = (choiceIndex: number) => {
  if (!props.previewMode) {
    localField.value.choiceValue =
      localField.value.options.choices![choiceIndex];
    if (localField.value.otherChoiceValue) {
      localField.value.otherChoiceValue!.value = '-1';
    }
  }

  localField.value.inputChanged = true;
};

const onOtherChoiceClick = () => {
  if (!props.previewMode) {
    localField.value.choiceValue = FormFieldChoice.createEmpty();
    localField.value.otherChoiceValue!.value = OTHER_CHOICE_CODE;
    localField.value.otherChoiceValue!.checked =
      !localField.value.otherChoiceValue!.checked;
  }

  localField.value.inputChanged = true;
};

const onCheckboxClick = (choiceIndex: number) => {
  if (!props.previewMode) {
    localField.value.options.choices![choiceIndex].checked =
      !localField.value.options.choices![choiceIndex].checked;
  }

  localField.value.inputChanged = true;
};

const otherChoiceInputDisable = computed(() => {
  if (
    props.previewMode ||
    localField.value.otherChoiceValue!.value !== OTHER_CHOICE_CODE ||
    (localField.value.options.maximumChoicesCount != 1 &&
      !localField.value.otherChoiceValue!.checked)
  ) {
    return true;
  }
  return false;
});
</script>

<template>
  <div class="item-root">
    <div class="single-choices"
      v-if="
        localField.options.maximumChoicesCount == 1 && localField.choiceValue
      ">
      <div v-for="(choice, index) in localField.options.choices"
        :key="choice.id"
        @click.prevent="onChoiceClick(index)"
        v-ripple>
        <div class="choice-item" v-if="!choice.archived">
          <RadioButton v-model="(localField.choiceValue as FormFieldChoice)!.value"
            name="single"
            :disabled="previewMode"
            :value="choice.value"/>
          <label for="single" class="choice-label">{{ choice.label }}</label>
          <FontAwesomeIcon :icon="[`fas`, `square-info`]"
            v-if="choice.tooltip"
            v-tooltip="choice.tooltip"
            class="lock"/>
        </div>
      </div>
    </div>

    <div class="single-choices"
      v-if="localField.options.maximumChoicesCount != 1">
      <div v-for="(choice, index) in localField.options.choices"
        :key="choice.id"
        @click.prevent="onCheckboxClick(index)"
        v-ripple>
        <div class="choice-item" v-if="!choice.archived">
          <Checkbox v-model="choice.checked"
            :binary="true"
            :disabled="previewMode"/>
          <label for="single" class="choice-label">{{ choice.label }}</label>
          <FontAwesomeIcon :icon="[`fas`, `square-info`]"
            v-if="choice.tooltip"
            v-tooltip="choice.tooltip"
            class="lock"/>
        </div>
      </div>
    </div>

    <div class="choice-item"
      v-if="localField.options.allowOther && localField.otherChoiceValue"
      @click.prevent="onOtherChoiceClick"
      v-ripple>
      <div @click.prevent="onOtherChoiceClick">
        <RadioButton v-model="localField.otherChoiceValue!.value"
          name="single"
          v-if="localField.options.maximumChoicesCount == 1"
          :disabled="previewMode"
          :value="OTHER_CHOICE_CODE"/>
        <Checkbox v-model="localField.otherChoiceValue!.checked"
          :binary="true"
          v-if="localField.options.maximumChoicesCount != 1"
          :disabled="previewMode"/>
        <label for="single" class="choice-label">{{
          $t('other')
        }}</label>
      </div>
      <InputText class="other-input"
        v-model="localField.otherChoiceValue!.otherValue"
        :disabled="otherChoiceInputDisable"
        type="text"/>
    </div>

    <CustomButton :text="$t('reset')"
      @onClick="resetChoiceValue"
      class="reset-button"
      v-if="!previewMode && !localField.strictRequired"
      :type="ButtonType.OUTLINED"/>
  </div>
</template>

<style scoped lang="scss">
.choice-item {
  display: flex;
  align-items: center;
  padding: 12px;

  &:hover {
    cursor: pointer;
    border-radius: 4px;
    background-color: #f5f7fa;
  }
}
.choice-label {
  padding: 0 12px;
  color: #272a2e;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    cursor: pointer;
  }
}
.reset-button {
  margin-top: 16px;
}
.other-input {
  font-size: 14px;
}
</style>
